/**
 * CreateEventModal — the Create Event dialog, per the Figma.
 *
 *   Create Event                                      ✕
 *
 *   Event Name
 *   [                                                  ]
 *
 *   Event Details
 *   (•) Do It Later      ( ) Fill in Details Now
 *
 *                                       Cancel  [ Create ]
 *
 * One field, because an event has ~15 writable attributes and asking for them up
 * front turns "I need this event on the calendar" into a form-filling session. The
 * service agrees: `CreateEvent` requires only `eventName` plus the owner, and the rest
 * is editable afterwards through `UpdateEvent`.
 *
 * The radio chooses where the user lands, not what gets sent — either way the event is
 * created as a DRAFT with just a name. The host routes on it; see `onCreated`'s `next`.
 *
 * Two things the Figma shows that are deliberately absent:
 *   - "Save as Draft" alongside Create. Dropped: both buttons would have created the
 *     same DRAFT, since a name-only event cannot be published (the service requires
 *     dates and, for nomination events, a window, before PublishEvent will pass).
 *   - "Parent Event Name" in the field label. The parent-event relationship is the
 *     campaign hierarchy (CampaignNode), which is a separate piece of work.
 */

import Alert from "@amzn/awsui-components-react/polaris/alert";
import Box from "@amzn/awsui-components-react/polaris/box";
import Button from "@amzn/awsui-components-react/polaris/button";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import RadioGroup from "@amzn/awsui-components-react/polaris/radio-group";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { useState } from "react";
import type { CreateEventInput, Event } from "../../client/EventCalendarClient";

/**
 * Where the user asked to go once the event exists — the "Event Details" radio.
 * `'later'` is the Figma's default.
 */
export type CreateEventNext = "later" | "now";

export interface CreateEventModalProps {
  visible: boolean;
  /**
   * Amazon alias recorded as the event owner. Required by `CreateEvent` and taken from
   * the signed-in user by the host rather than typed into the form.
   */
  owner: string;
  onDismiss: () => void;
  onSubmit: (input: CreateEventInput) => Promise<Event>;
  /**
   * Called once the event exists. `next` is the radio selection, so the host can route
   * accordingly; the event carries the server-generated id to route with.
   */
  onCreated: (event: Event, next: CreateEventNext) => void;
}

export function CreateEventModal({
  visible,
  owner,
  onDismiss,
  onSubmit,
  onCreated,
}: CreateEventModalProps) {
  const [name, setName] = useState("");
  const [next, setNext] = useState<CreateEventNext>("later");
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function reset() {
    setName("");
    setNext("later");
    setNameError(null);
    setSubmitError(null);
  }

  function dismiss() {
    reset();
    onDismiss();
  }

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Required");
      return;
    }
    setNameError(null);
    setSubmitting(true);
    setSubmitError(null);
    try {
      const event = await onSubmit({ eventName: trimmed, eventOwner: owner });
      reset();
      onCreated(event, next);
    } catch (e: unknown) {
      // The dialog stays open on failure so the typed name is not lost.
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={visible}
      onDismiss={dismiss}
      header="Create Event"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={dismiss} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} loading={submitting}>
              Create
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {submitError ? (
          <Alert type="error" header="Failed to create the event">
            {submitError}
          </Alert>
        ) : null}

        <FormField label="Event Name" errorText={nameError}>
          <Input
            value={name}
            onChange={({ detail }) => {
              setName(detail.value);
              if (nameError) setNameError(null);
            }}
            onKeyDown={({ detail }) => {
              if (detail.key === "Enter" && !submitting) void submit();
            }}
            placeholder="Placeholder"
            autoFocus
          />
        </FormField>

        <FormField label="Event Details">
          <div>
            <RadioGroup
              direction="horizontal"
              value={next}
              onChange={({ detail }) => setNext(detail.value as CreateEventNext)}
              items={[
                { value: "later", label: "Do It Later" },
                { value: "now", label: "Fill in Details Now" },
              ]}
            />
          </div>
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
